import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Shield,
  RefreshCw,
  ChevronLeft,
  Save,
  Trash2,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";

// ✅ Hooks & Utils
import { useAccess } from "../../../utils/useAccess";
import { PERMS } from "../../../utils/perms";
import AccessGate from "../../../components/AccessGate";

import { useGetDesignationQuery } from "../api/designationsApi";
import {
  PermissionLite,
  PermissionsByModule,
  useGetAllPermissionsQuery,
  useRevokeAllRolePermissionsMutation,
  useSyncRolePermissionsMutation,
} from "../../access/api/accessApi";
import PageGate from "../../../components/PageGate";

function titleCase(s: string) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function buildNameSet(names: string[]) {
  const map: Record<string, true> = {};
  names.forEach((n) => {
    const k = String(n || "").trim();
    if (k) map[k] = true;
  });
  return map;
}

function getErrMsg(err: any) {
  return (
    err?.data?.message || err?.data?.error || err?.error || "Request failed"
  );
}

export default function DesignationDetailsPage() {
  const navigate = useNavigate();
  const id = Number(useParams().id);
  const { can } = useAccess(); // ✅ Access control

  // ✅ Permissions logic
  const canSync = can.perm(PERMS.access?.sync);
  const canRevoke = can.perm(PERMS.access?.revoke);

  const {
    data: designation,
    isLoading: dLoading,
    error: dError,
    refetch: refetchDesignation,
  } = useGetDesignationQuery(id, { skip: !id });

  const {
    data: permsByModule,
    isLoading: permsLoading,
    error: permsError,
    refetch: refetchPerms,
  } = useGetAllPermissionsQuery();

  const [syncPerms, { isLoading: saving }] = useSyncRolePermissionsMutation();
  const [revokeAll, { isLoading: revoking }] =
    useRevokeAllRolePermissionsMutation();

  const roleId = designation?.role?.id;

  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [localOk, setLocalOk] = useState<string | null>(null);

  useEffect(() => {
    const current = (designation?.role?.permissions || []).map((p: any) =>
      String(p.name)
    );
    setSelectedNames(current);
  }, [designation]);

  const selectedSet = useMemo(
    () => buildNameSet(selectedNames),
    [selectedNames]
  );

  const modules: [string, PermissionLite[]][] = useMemo(() => {
    const obj: PermissionsByModule = permsByModule || {};
    return Object.entries(obj).sort(([a], [b]) => a.localeCompare(b));
  }, [permsByModule]);

  const loading = dLoading || permsLoading;
  const error = dError || permsError;

  const togglePermission = (permName: string) => {
    if (!canSync) return; // Prevent toggle if no permission
    setSelectedNames((prev) => {
      if (prev.includes(permName)) return prev.filter((x) => x !== permName);
      return [...prev, permName];
    });
  };

  const save = async () => {
    if (!roleId || !canSync) return;
    setLocalErr(null);
    setLocalOk(null);
    try {
      await syncPerms({ roleId, permissions: selectedNames }).unwrap();
      setLocalOk("Permissions updated successfully.");
      refetchDesignation();
    } catch (e: any) {
      setLocalErr(getErrMsg(e));
    }
  };

  const revokeAllNow = async () => {
    if (!roleId || !canRevoke) return;
    if (
      !window.confirm(
        `Revoke ALL permissions for role "${
          designation?.role?.name ?? "role"
        }"?`
      )
    )
      return;
    setLocalErr(null);
    setLocalOk(null);
    try {
      await revokeAll(roleId).unwrap();
      setSelectedNames([]);
      setLocalOk("All permissions revoked.");
      refetchDesignation();
    } catch (e: any) {
      setLocalErr(getErrMsg(e));
    }
  };

  return (
    <PageGate perm={PERMS.designation.detailsAccess}>
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white border rounded-xl p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-900 rounded-xl shadow-lg shadow-gray-200">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <button
                  onClick={() => navigate("/designations")}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-1"
                >
                  <ChevronLeft className="w-3 h-3" /> BACK TO DESIGNATIONS
                </button>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                  Access Matrix
                </h1>
                <p className="text-sm text-gray-500">
                  Configuring role:{" "}
                  <span className="font-bold text-gray-800">
                    {designation?.role?.name || "---"}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  refetchDesignation();
                  refetchPerms();
                }}
                className="p-2.5 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
                title="Refresh Data"
              >
                <RefreshCw
                  className={`w-4 h-4 text-gray-600 ${
                    loading ? "animate-spin" : ""
                  }`}
                />
              </button>

              <AccessGate perm={PERMS.access?.revoke}>
                <button
                  onClick={revokeAllNow}
                  disabled={!roleId || revoking}
                  className="px-4 py-2.5 rounded-xl border border-red-100 bg-red-50 text-red-700 hover:bg-red-100 text-xs font-bold transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" /> REVOKE ALL
                </button>
              </AccessGate>

              <AccessGate perm={PERMS.access?.sync}>
                <button
                  onClick={save}
                  disabled={!roleId || saving}
                  className="px-6 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 text-xs font-bold transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-gray-900/20"
                >
                  <Save className="w-4 h-4" />{" "}
                  {saving ? "SAVING..." : "SAVE CHANGES"}
                </button>
              </AccessGate>
            </div>
          </div>

          {/* Alerts */}
          {(localErr || localOk) && (
            <div
              className={`mt-6 p-4 rounded-xl border text-sm font-medium animate-in fade-in slide-in-from-top-2 ${
                localErr
                  ? "bg-red-50 border-red-200 text-red-800"
                  : "bg-green-50 border-green-200 text-green-800"
              }`}
            >
              <div className="flex items-center gap-2">
                {localErr ? (
                  <ShieldAlert className="w-4 h-4" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                {localErr || localOk}
              </div>
            </div>
          )}
        </div>

        {/* Meta Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Target Designation
            </div>
            <div className="text-lg font-bold text-gray-900 mt-1">
              {designation?.name ?? "—"}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Spatie Role Name
            </div>
            <div className="text-lg font-bold text-blue-600 mt-1">
              {designation?.role?.name ?? "—"}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Active Permissions
            </div>
            <div className="text-lg font-bold text-gray-900 mt-1">
              {selectedNames.length}{" "}
              <span className="text-sm font-normal text-gray-400">
                assigned
              </span>
            </div>
          </div>
        </div>

        {/* Body / Matrix */}
        {loading ? (
          <div className="py-20 text-center text-gray-400 font-medium">
            Building permission grid...
          </div>
        ) : error ? (
          <div className="py-20 text-center text-red-600 font-medium bg-white border rounded-xl shadow-sm">
            Failed to synchronize with access server.
          </div>
        ) : (
          <div className="space-y-8">
            {modules.map(([moduleName, perms]) => {
              const names = perms.map((p) => p.name);
              const selectedCount = perms.filter(
                (p) => !!selectedSet[p.name]
              ).length;
              const isAllSelected = selectedCount === perms.length;

              return (
                <div
                  key={moduleName}
                  className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm"
                >
                  {/* Module Header with dynamic shading */}
                  <div
                    className={`px-6 py-4 flex items-center justify-between border-b transition-colors ${
                      isAllSelected
                        ? "bg-green-50/50 border-green-100"
                        : "bg-gray-50/50 border-gray-100"
                    }`}
                  >
                    <div>
                      <h2 className="text-sm font-black text-gray-900 uppercase tracking-tighter">
                        {titleCase(moduleName)} Module
                      </h2>
                      <p
                        className={`text-[11px] font-bold mt-0.5 ${
                          isAllSelected ? "text-green-600" : "text-gray-400"
                        }`}
                      >
                        {selectedCount} of {perms.length} permissions active
                      </p>
                    </div>

                    <AccessGate perm={PERMS.access?.sync || PERMS.admin.access}>
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            setSelectedNames((prev) =>
                              Array.from(new Set([...prev, ...names]))
                            )
                          }
                          className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-[10px] font-bold text-gray-600 uppercase transition-all"
                        >
                          Select all
                        </button>
                        <button
                          onClick={() =>
                            setSelectedNames((prev) =>
                              prev.filter((x) => !names.includes(x))
                            )
                          }
                          className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-[10px] font-bold text-gray-600 uppercase transition-all"
                        >
                          Clear
                        </button>
                      </div>
                    </AccessGate>
                  </div>

                  {/* Permissions Grid */}
                  <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 bg-white">
                    {perms
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((p) => {
                        const checked = !!selectedSet[p.name];

                        return (
                          <label
                            key={p.id}
                            className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-all group ${
                              checked
                                ? "bg-blue-50/50 border-blue-200 shadow-sm"
                                : "hover:border-gray-300 border-gray-100"
                            } ${
                              !canSync ? "opacity-75 cursor-not-allowed" : ""
                            }`}
                          >
                            <div className="pt-0.5">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePermission(p.name)}
                                disabled={!canSync}
                                className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900 disabled:opacity-50"
                              />
                            </div>
                            <div className="min-w-0">
                              <div
                                className={`text-xs font-bold leading-tight ${
                                  checked ? "text-blue-900" : "text-gray-700"
                                }`}
                              >
                                {p.name.replace(/_/g, " ")}
                              </div>
                              <div className="text-[10px] text-gray-400 mt-1 font-mono uppercase truncate">
                                {p.action}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                  </div>
                </div>
              );
            })}

            {!modules.length && (
              <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed text-gray-400 text-sm">
                No system permissions mapped to registry.
              </div>
            )}
          </div>
        )}
      </div>
    </PageGate>
  );
}
